import { useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { AppButton, AppCard, AppField, AppInput, AppModal, ConfirmAction } from "../../../shared/components/ui";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { IconSpinner } from "../../../shared/components/icons";
import { api } from "../../../shared/api/client";

type DiscountRule = {
  id: number;
  name: string;
  payment_type: string | null;
  device_type_id: number | null;
  min_payment_amount: number | null;
  discount_percent: number;
  valid_from: string | null;
  valid_until: string | null;
};

function DiscountRuleModal({ open, rule, onClose, onSuccess }: {
  open: boolean;
  rule?: DiscountRule | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [form, setForm] = useState({
    name: "", discount_percent: "", payment_type: "", min_payment_amount: "",
    valid_from: "", valid_until: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) {
      setForm(rule ? {
        name: rule.name,
        discount_percent: String(rule.discount_percent),
        payment_type: rule.payment_type ?? "",
        min_payment_amount: rule.min_payment_amount != null ? String(rule.min_payment_amount) : "",
        valid_from: rule.valid_from?.slice(0, 10) ?? "",
        valid_until: rule.valid_until?.slice(0, 10) ?? "",
      } : {
        name: "", discount_percent: "", payment_type: "", min_payment_amount: "",
        valid_from: "", valid_until: "",
      });
      setErr("");
    }
  }, [open, rule]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || form.discount_percent === "") {
      setErr("Name and discount percent are required");
      return;
    }
    const pct = parseFloat(form.discount_percent);
    if (Number.isNaN(pct) || pct <= 0 || pct > 100) {
      setErr("Discount percent must be a number between 0 and 100");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const body = {
        name: form.name.trim(),
        discount_percent: pct,
        payment_type: form.payment_type || null,
        min_payment_amount: form.min_payment_amount ? parseFloat(form.min_payment_amount) : null,
        valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : null,
        valid_until: form.valid_until ? new Date(form.valid_until).toISOString() : null,
      };
      if (rule) await api.put(`/discount-rules/${rule.id}`, body);
      else await api.post("/discount-rules", body);
      onSuccess();
      onClose();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : `Failed to ${rule ? "update" : "create"} discount rule`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppModal open={open} onClose={onClose} title={rule ? "Edit Discount Rule" : "Create Discount Rule"}>
      <form onSubmit={submit}>
        <AppField label="Rule Name">
          <AppInput id="dr-name" type="text" placeholder="e.g. Cash upfront 5%" value={form.name} onChange={(e) => set("name", e.target.value)} disabled={busy} autoFocus />
        </AppField>
        <div className="pg-two-col-form">
          <AppField label="Discount Percent">
            <AppInput id="dr-percent" type="number" min={0} max={100} step="0.1" placeholder="e.g. 5" value={form.discount_percent} onChange={(e) => set("discount_percent", e.target.value)} disabled={busy} />
          </AppField>
          <AppField label="Payment Type (optional)">
            <AppInput id="dr-payment-type" type="text" placeholder="e.g. cash" value={form.payment_type} onChange={(e) => set("payment_type", e.target.value)} disabled={busy} />
          </AppField>
        </div>
        <AppField label="Minimum Payment Amount (optional)">
          <AppInput id="dr-min-amount" type="number" min={0} placeholder="TZS" value={form.min_payment_amount} onChange={(e) => set("min_payment_amount", e.target.value)} disabled={busy} />
        </AppField>
        <div className="pg-two-col-form">
          <AppField label="Valid From (optional)">
            <AppInput id="dr-valid-from" type="date" value={form.valid_from} onChange={(e) => set("valid_from", e.target.value)} disabled={busy} />
          </AppField>
          <AppField label="Valid Until (optional)">
            <AppInput id="dr-valid-until" type="date" value={form.valid_until} onChange={(e) => set("valid_until", e.target.value)} disabled={busy} />
          </AppField>
        </div>
        {err && <div className="pg-inline-error" role="alert">{err}</div>}
        <div className="pg-modal-actions">
          <AppButton type="submit" disabled={busy}>
            {busy ? <><IconSpinner size={14} /> Saving…</> : rule ? "Save Rule" : "Create Rule"}
          </AppButton>
          <AppButton type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</AppButton>
        </div>
      </form>
    </AppModal>
  );
}

export function DiscountRules() {
  const [rules, setRules] = useState<DiscountRule[]>([]);
  const [pageState, setPageState] = useState<"loading" | "error" | "success">("loading");
  const [pageError, setPageError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<DiscountRule | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState<DiscountRule | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  const load = async () => {
    setPageState("loading");
    try {
      const d = await api.get<{ discount_rules: DiscountRule[] }>("/discount-rules");
      setRules(d.discount_rules ?? []);
      setPageState("success");
    } catch (ex: unknown) {
      setPageError(ex instanceof Error ? ex.message : "Failed to load discount rules");
      setPageState("error");
    }
  };
  useEffect(() => { load(); }, []);

  const deactivate = async () => {
    if (!confirmDeactivate) return;
    setActionBusy(true);
    setActionError("");
    try {
      await api.delete(`/discount-rules/${confirmDeactivate.id}`);
      setConfirmDeactivate(null);
      await load();
    } catch (ex: unknown) {
      setActionError(ex instanceof Error ? ex.message : "Failed to deactivate discount rule");
    } finally {
      setActionBusy(false);
    }
  };

  const columns: ColumnDef<DiscountRule, unknown>[] = [
    { accessorKey: "name", header: "Name" },
    { id: "payment_type", header: "Payment Type", accessorFn: (r) => r.payment_type ?? "Any" },
    { id: "min_amount", header: "Min Amount (TZS)", accessorFn: (r) => r.min_payment_amount != null ? r.min_payment_amount.toLocaleString() : "—" },
    { id: "discount", header: "Discount", accessorFn: (r) => `${r.discount_percent}%` },
    { id: "valid_from", header: "Valid From", accessorFn: (r) => r.valid_from ? new Date(r.valid_from).toLocaleDateString("en-GB") : "—" },
    { id: "valid_until", header: "Valid Until", accessorFn: (r) => r.valid_until ? new Date(r.valid_until).toLocaleDateString("en-GB") : "—" },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => (
        <div className="pg-row-actions">
          <AppButton type="button" variant="secondary" onClick={() => { setActionError(""); setEditing(row.original); }}>
            Edit
          </AppButton>
          <AppButton type="button" variant="secondary" disabled={actionBusy} onClick={() => { setActionError(""); setConfirmDeactivate(row.original); }}>
            Deactivate
          </AppButton>
        </div>
      ),
    },
  ];

  const listStatus =
    pageState === "success" && rules.length === 0 ? "empty" : pageState;

  return (
    <div>
      <DiscountRuleModal open={createOpen} onClose={() => setCreateOpen(false)} onSuccess={load} />
      <DiscountRuleModal open={!!editing} rule={editing} onClose={() => setEditing(null)} onSuccess={load} />

      <PageHeader
        title="Discount Rules"
        subtitle="Selectable at Create Contract (percent off original total). Not applied only at payment time."
        actions={<AppButton type="button" onClick={() => setCreateOpen(true)}>Create Rule</AppButton>}
      />
      {actionError && <div className="pg-inline-error" role="alert">{actionError}</div>}

      <PageState
        status={listStatus}
        error={pageError}
        onRetry={load}
        emptyTitle="No discount rules"
        emptyHint="Create a rule to apply automatic discounts."
      >
        <AppCard>
          <div className="pg-table-meta"><span>{rules.length} rule{rules.length !== 1 ? "s" : ""}</span></div>
          <AppDataTable data={rules} columns={columns} />
        </AppCard>
      </PageState>
      <ConfirmAction
        open={!!confirmDeactivate}
        title="Deactivate discount rule?"
        message={confirmDeactivate ? `${confirmDeactivate.name} will no longer be available for new contracts.` : ""}
        confirmLabel="Deactivate"
        danger
        busy={actionBusy}
        onCancel={() => setConfirmDeactivate(null)}
        onConfirm={() => void deactivate()}
      />
    </div>
  );
}
